import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Products from './pages/Products';
import Plans from './pages/Plans';
import Dashboard from './pages/Dashboard';
import Ranks from './pages/Ranks';
import Income from './pages/Income';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Payouts from './pages/Payouts';
import Settings from './pages/Settings';
import Network from './pages/Network';
import AdminPanel from './pages/AdminPanel';
import ErrorBoundary from './components/ErrorBoundary';
import AdminLayout from './components/admin/AdminLayout';
import UserLayout from './components/UserLayout';
import './styles/scroll-behavior.css';
import UserProducts from './components/user/UserProducts';
import UserKYC from './components/user/UserKYC';
import BonanzaTracker from './pages/BonanzaTracker';
import UserFundTransferHistory from './components/user/UserFundTransferHistory';
import UserInvestmentPlans from './components/UserInvestmentPlans';

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Smooth scroll to top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    
    // Fallback for browsers that don't support smooth scrolling
    if (!('scrollBehavior' in document.documentElement.style)) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
};

function App() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Header />
      <ErrorBoundary>
        <main className={isAuthenticated ? 'bg-gray-50 min-h-screen' : ''}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/products" element={<UserProducts />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Protected Routes */}
            {isAuthenticated ? (
              <>
                <Route path="/dashboard" element={<UserLayout />} />
                
                {/* User Dashboard Routes */}
                <Route path="/user" element={<UserLayout />} />
                <Route path="/user/products" element={<UserLayout />} />
                <Route path="/user/cart" element={<UserLayout />} />
                <Route path="/user/ranks" element={<UserLayout />} />
                <Route path="/user/income" element={<UserLayout />} />
                <Route path="/user/purchases" element={<UserLayout />} />
                <Route path="/user/payouts" element={<UserLayout />} />
                <Route path="/user/settings" element={<UserLayout />} />
                <Route path="/user/kyc" element={<UserLayout />} />
                <Route path="/user/downline" element={<UserLayout />} />
                <Route path="/user/team-15" element={<UserLayout />} />
                <Route path="/user/send-money" element={<UserLayout />} />
                <Route path="/user/reset-password" element={<UserLayout />} />
                <Route path="/user/fund-transfer" element={<UserLayout />} />
                <Route path="/user/main-to-deposit" element={<UserLayout />} />
                <Route path="/user/bonanza" element={<UserLayout />} />
                <Route path="/user/fund-transfer-history" element={<UserLayout />} />
                <Route path="/user/investment-plans" element={<UserLayout />} />
                <Route path="/user/lifetime-rewards" element={<UserLayout />} />
                
                {/* Legacy Routes */}
                <Route path="/ranks" element={<Ranks />} />
                <Route path="/income" element={<Income />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/payouts" element={<Payouts />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/network" element={<Network />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout  />} />
                <Route path="/admin/users" element={<AdminLayout  />} />
                <Route path="/admin/products" element={<AdminLayout  />} />
                <Route path="/admin/payouts" element={<AdminLayout  />} />
                <Route path="/admin/reports" element={<AdminLayout  />} />
                <Route path="/admin/payment-history" element={<AdminLayout  />} />
                <Route path="/admin/investment-plans" element={<AdminLayout  />} />
        <Route path="/admin/lifetime-rewards" element={<AdminLayout  />} />
              </>
            ) : (
              /* Redirect all other routes to login for unauthenticated users */
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </main>
      </ErrorBoundary>
      <Footer />
    </Router>
  );
}

export default App;
